import { ChecklistItem } from '@app/app/_components/ChecklistItem/ChecklistItem';
import { ChoiceButtonInput, ChoiceButtonOption } from '@app/app/_components/Input/ChoiceButtonInput';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { LifePackage } from '@app/domain/model/eshop/life/LifePackage';
import { LifePackageCode } from '@app/domain/model/eshop/life/LifePackageCode';
import { formatEuroValue } from '@app/infrastructure/application/formatEuroValue';
import { determineLifePackageTitle } from '@app/infrastructure/eshop/transport/determineLifePackageTitle';
import { determineCoverageName } from '@app/infrastructure/policies/determineCoverageName';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useState } from 'react';
import { CallbackButton } from '@app/app/_components/CallbackButton/CallbackButton';
import { PackageDetailsView } from '@app/app/esales/life/_sections/steps/PackageDetailsView';
import { SessionResponse } from '@app/domain/model/eshop/life/session/SessionResponse';
import { QuestionCircleIcon } from '@app/app/_icons/QuestionCircleIcon';
import { Tag } from '@app/app/_components/Tag/Tag';
import { InformationPopover } from '@app/app/_components/InformationPopover/InformationPopover';
import { useDebouncedCallback } from 'use-debounce';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';

interface Props {
    packages: LifePackage[];
    sessionData: Nullable<SessionResponse>;
    onPackageChange: () => void;
}

export function ChoosePackageStep({ packages, sessionData, onPackageChange }: Props) {
    const { tenant } = useAppParameters();
    const t = useTranslations();
    const [selectedPackageForDetails, setSelectedPackageForDetails] = useState<Nullable<LifePackage>>(null);
    const [isDetailsViewOpen, setIsDetailsViewOpen] = useState(false);

    const debouncedPackageChange = useDebouncedCallback(() => {
        void onPackageChange();
    }, 500);

    const defaultPackageCode = useMemo(() => {
        if (sessionData?.firstStep?.selectedPackage) {
            return sessionData.firstStep.selectedPackage;
        }
        const defaultPackage = packages.find(pkg => pkg.default);
        return defaultPackage?.code;
    }, [packages, sessionData]);

    const handleReadMoreClick = useCallback(
        (packageCode: string) => {
            const packageItem = packages.find(pkg => pkg.code === packageCode) || null;
            setSelectedPackageForDetails(packageItem);
            setIsDetailsViewOpen(true);
        },
        [packages],
    );

    const handleCloseDetailsView = useCallback(() => {
        setIsDetailsViewOpen(false);
        setSelectedPackageForDetails(null);
    }, []);

    const packageOptions: ChoiceButtonOption[] = useMemo(() => {
        return packages.map(element => {
            const isFreePackage = element.code === LifePackageCode.Free;
            const nonAccidentCovers = element.covers.filter(cover => !cover.isAccident);
            const accidentCovers = element.covers.filter(cover => cover.isAccident);

            return {
                label: (
                    <div className="flex flex-col gap-2">
                        {element.code === defaultPackageCode && (
                            <div className="flex items-center gap-1">
                                <Tag variant="suggestion">{t('general.suggested')}</Tag>
                                <InformationPopover content={t('lifeInsurance.suggestedPackageDescription')}>
                                    <QuestionCircleIcon className="size-6 text-neutral-400" />
                                </InformationPopover>
                            </div>
                        )}
                        <Paragraph size="medium">{t(determineLifePackageTitle(element.code))}</Paragraph>
                        <Paragraph>
                            {isFreePackage
                                ? t('eshop.life.packages.monthlyPaymentAccordingToOptions')
                                : `${formatEuroValue(element.premium, tenant)} / ${t('general.month').toLowerCase()}`}
                        </Paragraph>
                    </div>
                ),
                value: element.code,
                disabled: !element.available,
                details: isFreePackage ? (
                    <Paragraph>{t('eshop.life.packages.chooseProtectionsInNextStep')}</Paragraph>
                ) : (
                    <div className="flex flex-col gap-2">
                        {nonAccidentCovers.map((cover, index) => (
                            <ChecklistItem key={`${cover.code}-${index}`}>
                                {`${t(determineCoverageName(cover.code))}: ${formatEuroValue(cover.default, tenant, true)}`}
                            </ChecklistItem>
                        ))}
                        {accidentCovers.length > 0 && (
                            <>
                                <Paragraph>{t('eshop.life.packages.accident')}</Paragraph>
                                {accidentCovers.map((cover, index) => (
                                    <ChecklistItem key={`accident-${cover.code}-${index}`}>
                                        <span dangerouslySetInnerHTML={{ __html: cover.description }} />
                                    </ChecklistItem>
                                ))}
                            </>
                        )}
                        <CallbackButton
                            ignoreFormStatus
                            className="mx-0 self-start p-0"
                            variant="text"
                            id={element.code}
                            rightAddon={<ArrowRightIcon className="size-5" />}
                            onClick={handleReadMoreClick}>
                            {t('general.readMore')}
                        </CallbackButton>
                    </div>
                ),
            };
        });
    }, [packages, defaultPackageCode, t, handleReadMoreClick, tenant]);

    return (
        <>
            <ChoiceButtonInput
                options={packageOptions}
                name="selectedPackage"
                defaultChecked={defaultPackageCode}
                onChange={debouncedPackageChange}
            />
            <PackageDetailsView
                selectedPackage={selectedPackageForDetails}
                open={isDetailsViewOpen}
                onClose={handleCloseDetailsView}
            />
        </>
    );
}
